import React, { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import './FreshMeetSiteCms.css';

const PAGES = [
  { label: 'Home', filename: 'Home.jsx' },
  { label: 'Schedule', filename: 'Schedule.jsx' },
  { label: 'Dive Pool', filename: 'DivePool.jsx' },
  { label: 'Rules', filename: 'Rules.jsx' },
  { label: 'FAQ', filename: 'FAQ.jsx' },
  { label: 'Archive', filename: 'Archive.jsx' },
];

export default function FreshMeetSiteCms() {
  const [filename, setFilename] = useState(PAGES[0].filename);
  const [currentFromRepo, setCurrentFromRepo] = useState('');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const loadPage = useCallback(async (name) => {
    setError('');
    setStatus('');
    setLoading(true);
    try {
      const getFn = httpsCallable(functions, 'getFreshMeetPage');
      const res = await getFn({ filename: name });
      const content = res.data?.content ?? '';
      setCurrentFromRepo(content);
      setDraft(content);
    } catch (err) {
      setCurrentFromRepo('');
      setDraft('');
      setError(err.message || 'Failed to load page from GitHub.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectPage = (e) => {
    setFilename(e.target.value);
  };

  React.useEffect(() => {
    loadPage(filename);
  }, [filename, loadPage]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentFromRepo);
      setStatus('Copied current file to clipboard.');
    } catch {
      setError('Could not copy to clipboard.');
    }
  };

  const handlePush = async () => {
    setError('');
    setStatus('');
    setPushing(true);
    try {
      const pushFn = httpsCallable(functions, 'pushFreshMeetPage');
      await pushFn({ filename, content: draft });
      setStatus('Pushed to GitHub. The site will update when the deploy action finishes (usually 1–3 minutes).');
      await loadPage(filename);
    } catch (err) {
      setError(err.message || 'Push failed.');
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="freshmeet-cms">
      <h1 className="freshmeet-cms__title">Fresh Meet site (pages)</h1>
      <p className="freshmeet-cms__intro">
        Copy JSX for an external edit, paste changes below, then push live. Only files under{' '}
        <code>src/pages/</code> are editable; navigation in <code>App.jsx</code> is not changed here.
      </p>

      <div className="freshmeet-cms__row">
        <label className="freshmeet-cms__label" htmlFor="freshmeet-page-select">
          Page
        </label>
        <select
          id="freshmeet-page-select"
          className="freshmeet-cms__select"
          value={filename}
          onChange={handleSelectPage}
          disabled={loading || pushing}
        >
          {PAGES.map((p) => (
            <option key={p.filename} value={p.filename}>
              {p.label} ({p.filename})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="freshmeet-cms__btn freshmeet-cms__btn--secondary"
          onClick={() => loadPage(filename)}
          disabled={loading || pushing}
        >
          Reload from GitHub
        </button>
      </div>

      {error ? <div className="freshmeet-cms__msg freshmeet-cms__msg--error">{error}</div> : null}
      {status ? <div className="freshmeet-cms__msg freshmeet-cms__msg--ok">{status}</div> : null}

      <section className="freshmeet-cms__section">
        <div className="freshmeet-cms__sectionHead">
          <h2 className="freshmeet-cms__h2">Current file (read-only)</h2>
          <button
            type="button"
            className="freshmeet-cms__btn freshmeet-cms__btn--secondary"
            onClick={handleCopy}
            disabled={loading || !currentFromRepo}
          >
            Copy
          </button>
        </div>
        {loading ? (
          <p className="freshmeet-cms__hint">Loading…</p>
        ) : (
          <pre className="freshmeet-cms__pre">{currentFromRepo}</pre>
        )}
      </section>

      <section className="freshmeet-cms__section">
        <h2 className="freshmeet-cms__h2">Edited JSX (paste here)</h2>
        <textarea
          className="freshmeet-cms__textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={pushing}
          spellCheck={false}
          rows={24}
        />
        <div className="freshmeet-cms__actions">
          <button
            type="button"
            className="freshmeet-cms__btn freshmeet-cms__btn--primary btn-fm-primary"
            onClick={handlePush}
            disabled={pushing || loading}
          >
            {pushing ? 'Pushing…' : 'Push live'}
          </button>
        </div>
      </section>
    </div>
  );
}
